import { useMemo } from 'react';

import { isGroupRow } from '@app/components/common/table/utils';
import { Transaction, TransactionPackageDetail } from '@app/api/models/transaction';
import { useAlert, useApi } from '@app/hooks';

import commissioningBp from '../bproc/commissioning.json';
import decommissioningBp from '../bproc/decommissioning.json';
import installingBp from '../bproc/installing.json';
import movingBp from '../bproc/moving.json';
import removalBp from '../bproc/removal.json';
import correctionBp from '../bproc/correction.json';
import { ConfirmDialog } from '../../actions/confirm';
import { CancelDialog } from '../../actions/cancel';
import { DeclineDialog } from '../../actions/decline';
import { ApproveDialog } from '../../actions/approve';
import { SendEquipDialog } from '../../actions/sendEquip';
import { WaitDialog } from '../../actions/wait';
import { ConfirmManagerDialog } from '../../actions/confirmManager';
import { statuses } from '../mock/statuses';

export type ActionsProps = {
  selectedTx?: Transaction | TransactionPackageDetail;
  packageId: string;
  onChange?: (Transaction: Transaction | TransactionPackageDetail) => Promise<void>;
  onAction(): void;
};

export const ActionsComponent = ({ selectedTx, onChange, onAction, packageId }: ActionsProps) => {
  const api = useApi();
  const { success } = useAlert();
  const isPackage = useMemo(() => {
    if (!selectedTx) {return false;}
    return !!selectedTx.transactions;
  }, [selectedTx]);

  const txType = useMemo(() => {
    if (!selectedTx) {return null;}
    return selectedTx.type;
  }, [selectedTx]);

  const status = useMemo(() => {
    if (!selectedTx) {return null;}
    return (selectedTx as Transaction).status;
  }, [selectedTx]);

  const bp = useMemo(() => {
    switch(txType.id) {
      case 1: return commissioningBp;
      case 2: return movingBp;
      case 3: return installingBp;
      case 4: return removalBp;
      case 5: return correctionBp;
      case 6: return decommissioningBp;
    }
  }, [txType]);

  const stage = useMemo(() => { 
    return bp.find(({ status_id }) => status_id === status.id);
  }, [bp, status]);


  const getAction = (actionType: string) => async (selectedTx: Transaction | TransactionPackageDetail, comment?: string) => {
    const { next } = stage.actions.find(({ type }) => type === actionType);
    const nextStage = bp.find(({ id }) => id === next);
    const status = statuses.find(({ id }) => id === nextStage.status_id);

    if (isPackage) {
      const response = await api.transaction.updatePackage({
        package_id: packageId,
        status_id: status.id,
        comment,
      });
      if (response?.code === 200) {
        success(response.message);
        onAction();
  
        await onChange(selectedTx);
        return;
      }
      return;
    }

    const response = await api.transaction.update({
      transaction_id: Number((selectedTx as Transaction).id),
      status_id: status.id,
      comment,
      ...(actionType === 'send-equip' ? { equip_id: (selectedTx as Transaction).equip.id } : {}),
      ...(actionType === 'confirm-manager' ? { distributor_id: Number((selectedTx as Transaction).distributor.id) } : {}),
    });

    if (response?.code === 200) {
      success(response.message);
      onAction();

      await onChange(selectedTx);
      return;
    }
  };

  const components = useMemo(() => {
    return stage?.actions?.map(({ type, name }) => {
      if (type === 'approve') {
        return { Component: ApproveDialog, type, name };
      }
      if (type === 'decline') {
        return { Component: DeclineDialog, type, name };
      }
      if (type === 'confirm') {
        return { Component: ConfirmDialog, type, name };
      }
      if (type === 'confirm-manager') {
        return { Component: ConfirmManagerDialog, type, name };
      }
      if (type === 'cancel') {
        return { Component: CancelDialog, type, name };
      }
      if (type === 'wait') {
        return { Component: WaitDialog, type, name };
      }
      if (type === 'send-equip') {
        return { Component: SendEquipDialog, type, name };
      }
      throw new Error('Invalid type specified: ' + type);
    });
  }, [stage]);

  if (stage) {
    return <>
      {components.map(({ Component, type, name }, index) => <Component 
        key={index}
        selectedTx={selectedTx}
        onAction={getAction(type)}
        isPackage={isPackage}
        actionName={name}
      />)}
    </>;
  }

  return <></>;
};